import React, { useState, useEffect, useRef, useMemo } from 'react';
import { dashboardAPI } from '../services/apiClient';

const OverviewCard = ({ icon, title, value, backgroundColor, isLoading, error }) => (
  <div className="col s12 m4 top-insight">
    <div className="card" style={{ backgroundColor }}>
      <div className="card-content">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <i className={`${icon} fas`} style={{ marginRight: '8px', fontSize: '32px' }}></i>
          <span className="card-title">{title}</span>
        </div>
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
            <div className="preloader-wrapper small active">
              <div className="spinner-layer spinner-white-only">
                <div className="circle-clipper left">
                  <div className="circle"></div>
                </div>
                <div className="gap-patch">
                  <div className="circle"></div>
                </div>
                <div className="circle-clipper right">
                  <div className="circle"></div>
                </div>
              </div>
            </div>
            <span style={{ marginLeft: '10px', color: 'white' }}>Loading...</span>
          </div>
        ) : error ? (
          <div style={{ color: '#ffcccb', marginTop: '10px' }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: '5px' }}></i>
            Error loading data
          </div>
        ) : value !== null && value !== undefined ? (
          <h4>{value}</h4>
        ) : (
          <div style={{ color: '#ccc', marginTop: '10px', fontStyle: 'italic' }}>
            No data available
          </div>
        )}
      </div>
    </div>
  </div>
);

const Overview = ({ dateRange, onSearchClick }) => {
  const [data, setData] = useState({
    totalTillSlips: null,
    validHillsSlips: null,
    invalidSlips: null,
    totalSales: null,
    averageDiscount: null,
    uniqueStores: null,
    validatorAccuracy: null,
    overClaimed: null,
    underPaid: null
  });

  const [loading, setLoading] = useState({
    totalTillSlips: false,
    validHillsSlips: false,
    invalidSlips: false,
    totalSales: false,
    averageDiscount: false,
    uniqueStores: false,
    validatorAccuracy: false,
    overClaimed: false,
    underPaid: false
  });

  const [errors, setErrors] = useState({
    totalTillSlips: false,
    validHillsSlips: false,
    invalidSlips: false,
    totalSales: false,
    averageDiscount: false,
    uniqueStores: false,
    validatorAccuracy: false,
    overClaimed: false,
    underPaid: false
  });

  // Create a stable key from dateRange to prevent unnecessary re-renders
  const dateRangeKey = useMemo(() => {
    return `${dateRange?.startDate || ''}_${dateRange?.endDate || ''}_${dateRange?.dateFilterType || 'Redemption'}`;
  }, [dateRange?.startDate, dateRange?.endDate, dateRange?.dateFilterType]);

  // Track previous dateRange to prevent unnecessary refetches
  const prevDateRangeKeyRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Generic API call function - NOT memoized to avoid dependency issues
  const fetchData = async (apiCall, dataKey, formatter = (value) => value, currentDateRange) => {
    setLoading(prev => ({ ...prev, [dataKey]: true }));
    setErrors(prev => ({ ...prev, [dataKey]: false }));
    
    try {
      const response = await apiCall(currentDateRange);
      const result = response.data;
      
      // Handle different response structures
      if (!result) {
        console.warn(`[Overview] Empty response for ${dataKey}`);
        setData(prev => ({ ...prev, [dataKey]: 'N/A' }));
        return;
      }
      
      const formattedValue = formatter(result);
      setData(prev => ({ ...prev, [dataKey]: formattedValue }));
    } catch (error) {
      console.error(`[Overview] Error fetching data for ${dataKey}:`, error);
      setErrors(prev => ({ ...prev, [dataKey]: true }));
      // Set a default error value instead of leaving it null
      setData(prev => ({ ...prev, [dataKey]: 'Error' }));
    } finally {
      setLoading(prev => ({ ...prev, [dataKey]: false }));
    }
  };

  // Data formatters with error handling
  const formatCurrency = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return `R${numValue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatPercentage = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    return `${numValue.toFixed(1)}%`;
  };
  const formatNumber = (value) => {
    const numValue = typeof value === 'number' ? value : parseInt(value) || 0;
    return numValue.toLocaleString();
  };

  // Fetch all data using the authenticated API client with individual endpoints
  useEffect(() => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    // Check if dateRange actually changed to prevent infinite loops
    if (dateRangeKey === prevDateRangeKeyRef.current && prevDateRangeKeyRef.current !== null) {
      return;
    }
    
    // Mark as fetching and update the ref
    isFetchingRef.current = true;
    prevDateRangeKeyRef.current = dateRangeKey;
    
    // Create a stable dateRange object for this fetch
    const currentDateRange = {
      startDate: dateRange?.startDate || '',
      endDate: dateRange?.endDate || '',
      dateFilterType: dateRange?.dateFilterType || 'Redemption'
    };
    
    // Individual API calls for each overview metric (matching OpenAPI spec)
    Promise.all([
      fetchData(dashboardAPI.getTotalSlips, 'totalTillSlips', (result) => formatNumber(result.total || 0), currentDateRange),
      fetchData(dashboardAPI.getTotalValidSlips, 'validHillsSlips', (result) => formatNumber(result.total || 0), currentDateRange),
      fetchData(dashboardAPI.getTotalInvalidSlips, 'invalidSlips', (result) => formatNumber(result.total || 0), currentDateRange),
      fetchData(dashboardAPI.getTotalHillsSales, 'totalSales', (result) => formatCurrency(result.total || 0), currentDateRange),
      fetchData(dashboardAPI.getAverageDiscountValue, 'averageDiscount', (result) => formatCurrency(result.average || 0), currentDateRange),
      fetchData(dashboardAPI.getUniqueVetStoreCount, 'uniqueStores', (result) => formatNumber(result.count || 0), currentDateRange),
      fetchData(dashboardAPI.getValidatorAccuracy, 'validatorAccuracy', (result) => {
        // Try different possible response structures
        // Validator Accuracy should have max 2 decimal places
        const formatValidatorAccuracy = (value) => {
          const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
          return `${numValue.toFixed(2)}%`;
        };
        if (result.accuracy !== undefined) {
          return formatValidatorAccuracy(result.accuracy);
        } else if (result.count !== undefined) {
          return formatValidatorAccuracy(result.count);
        } else if (result.percentage !== undefined) {
          return formatValidatorAccuracy(result.percentage);
        } else if (result.value !== undefined) {
          return formatValidatorAccuracy(result.value);
        } else if (typeof result === 'number') {
          return formatValidatorAccuracy(result);
        } else {
          console.warn('Unknown validator accuracy response structure:', result);
          return 'N/A';
        }
      }, currentDateRange),
      fetchData(dashboardAPI.getOverClaimedTotal, 'overClaimed', (result) => formatCurrency(result.average || 0), currentDateRange),
      fetchData(dashboardAPI.getUnderPaidTotal, 'underPaid', (result) => formatCurrency(result.total || 0), currentDateRange)
    ]).finally(() => {
      isFetchingRef.current = false;
    });
  }, [dateRangeKey]); // Only depend on the memoized key

  return (
    <div className="section-box">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <img 
            src="https://www.hillspet.co.za/content/dam/cp-sites-aem/hills/hills-pet/mlp-assets/hills-logo.png"
            alt="Hills Pet Logo"
            style={{
              height: '40px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
          <div className="section-title">Overview</div>
        </div>
      </div>
      
      {/* Row 1: Till Slips */}
      <div className="row">
        <OverviewCard
          icon="fa-file-invoice"
          title="Total Till Slips"
          value={data.totalTillSlips}
          backgroundColor="#002E6D"
          isLoading={loading.totalTillSlips}
          error={errors.totalTillSlips}
        />
        <OverviewCard
          icon="fa-check-circle"
          title="Valid Hills Slips"
          value={data.validHillsSlips}
          backgroundColor="#4CAF50"
          isLoading={loading.validHillsSlips}
          error={errors.validHillsSlips}
        />
        <OverviewCard
          icon="fa-times-circle"
          title="Invalid/Incorrect Slips"
          value={data.invalidSlips}
          backgroundColor="#CE060D"
          isLoading={loading.invalidSlips}
          error={errors.invalidSlips}
        />
      </div>

      {/* Row 2: Sales, Discount, Unique Stores */}
      <div className="row">
        <OverviewCard
          icon="fa-dollar-sign"
          title="Total Hills Product Sales"
          value={data.totalSales}
          backgroundColor="#002E6D"
          isLoading={loading.totalSales}
          error={errors.totalSales}
        />
        <OverviewCard
          icon="fa-percent"
          title="Average Discount Value"
          value={data.averageDiscount}
          backgroundColor="#0077C8"
          isLoading={loading.averageDiscount}
          error={errors.averageDiscount}
        />
        <OverviewCard
          icon="fa-store"
          title="Unique Vet Stores"
          value={data.uniqueStores}
          backgroundColor="#4CAF50"
          isLoading={loading.uniqueStores}
          error={errors.uniqueStores}
        />
      </div>

      {/* Row 3: Validator & Payment Adjustments */}
      <div className="row">
        <OverviewCard
          icon="fa-user-check"
          title="Validator Accuracy"
          value={data.validatorAccuracy}
          backgroundColor="#002E6D"
          isLoading={loading.validatorAccuracy}
          error={errors.validatorAccuracy}
        />
        <OverviewCard
          icon="fa-arrow-up"
          title="Over Claimed"
          value={data.overClaimed}
          backgroundColor="#FF9800"
          isLoading={loading.overClaimed}
          error={errors.overClaimed}
        />
        <OverviewCard
          icon="fa-arrow-down"
          title="Under Paid"
          value={data.underPaid}
          backgroundColor="#FF9800"
          isLoading={loading.underPaid}
          error={errors.underPaid}
        />
      </div>
    </div>
  );
};

export default Overview; 